import { capabilityItems } from '@/data/marketing/capabilities';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';

// Map capability IDs to custom brand icons
const capabilityIcons: Record<string, string> = {
  'sales-lead-capture': '/icons/capabilities/icon-sales-lead-capture.png',
  'internal-information-delivery': '/icons/capabilities/icon-internal-info.png',
  'product-support': '/icons/capabilities/icon-product-support.png',
  'voice-experiences': '/icons/capabilities/icon-voice-experiences.png',
};

const CapabilitiesDropdown = () => {
  return (
    <NavigationMenu>
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger className="bg-transparent text-white text-base font-medium hover:bg-white/10 hover:text-white focus:bg-white/10 data-[state=open]:bg-white/10 px-2 py-2 h-auto">
            Capabilities
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="w-[min(640px,calc(100vw-2rem))] bg-white/95 backdrop-blur-xl border border-gray-200 rounded-xl p-4 md:p-6 shadow-2xl">
              <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                {/* How It Works - Left Column */}
                <div className="md:w-[180px] lg:w-[200px] shrink-0 md:border-r border-b md:border-b-0 border-gray-200 pb-4 md:pb-0 md:pr-6">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                    Learn More
                  </h3>
                  <div className="space-y-2">
                    <NavigationMenuLink asChild>
                      <a
                        href="/how-it-works"
                        className={cn(
                          "group flex flex-col gap-2 p-3 rounded-xl transition-all duration-200",
                          "hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg">
                            <img src="/icons/capabilities/icon-how-it-works.png" alt="How It Works" className="w-6 h-6 object-contain" />
                          </div>
                        </div>
                        <div className="space-y-0.5">
                          <h4 className="text-gray-900 font-semibold text-sm group-hover:text-purple-600 transition-colors">
                            How It Works
                          </h4>
                          <p className="text-gray-500 text-xs line-clamp-2 leading-relaxed">
                            Setup, knowledge, and deployment, end to end.
                          </p>
                        </div>
                      </a>
                    </NavigationMenuLink>
                    <NavigationMenuLink asChild>
                      <a
                        href="/playbooks"
                        className={cn(
                          "group flex flex-col gap-2 p-3 rounded-xl transition-all duration-200",
                          "hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg">
                            <img src="/icons/capabilities/icon-playbooks.png" alt="Playbooks" className="w-6 h-6 object-contain" />
                          </div>
                        </div>
                        <div className="space-y-0.5">
                          <h4 className="text-gray-900 font-semibold text-sm group-hover:text-purple-600 transition-colors">
                            Playbooks
                          </h4>
                          <p className="text-gray-500 text-xs line-clamp-2 leading-relaxed">
                            Voice conversation strategies that convert.
                          </p>
                        </div>
                      </a>
                    </NavigationMenuLink>
                  </div>
                </div>

                {/* Capabilities - Right Column */}
                <div className="flex-1">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                    What You Can Do
                  </h3>
                  <div className="space-y-3">
                    {capabilityItems.map((item) => {
                      const iconSrc = capabilityIcons[item.id];
                      return (
                        <NavigationMenuLink key={item.id} asChild>
                          <a
                            href={item.href}
                            className={cn(
                              "group flex flex-col gap-2 p-3 rounded-xl transition-all duration-200",
                              "hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                            )}
                          >
                            <div className="p-1.5 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg w-fit">
                              <img src={iconSrc} alt={item.name} className="w-6 h-6 object-contain" />
                            </div>
                            <div className="space-y-0.5">
                              <h4 className="text-gray-900 font-semibold text-sm group-hover:text-purple-600 transition-colors">
                                {item.name}
                              </h4>
                              <p className="text-gray-500 text-xs line-clamp-2 leading-relaxed">
                                {item.tagline}
                              </p>
                            </div>
                          </a>
                        </NavigationMenuLink>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default CapabilitiesDropdown;
